import { BiLoaderAlt } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

const Button = React.forwardRef(
  (
    {
      variant,
      className,
      linkTo,
      size,
      fontSize,
      fontWeight,
      children,
      onClick,
      submit,
      disabled,
      isLoading,
      ...props
    },
    ref
  ) => {
    const mainClass = 'transition-all rounded-full flex justify-center items-center font-medium tracking-wide';
    const sizeClass = classNames({
      'py-2 px-6': size === Button.SIZES.SMALL2X,
      'p-3 md:py-2 md:px-8': size === Button.SIZES.SMALL,
      'p-3 min-w-28': size === Button.SIZES.MEDIUM,
      'py-3 px-3': size === Button.SIZES.SQUARE,
      'py-4 px-11': size === Button.SIZES.LARGE,
      'py-4 px-12': size === Button.SIZES.LARGE2X,
      'h-12 px-6 py-[14px]': size === Button.SIZES.CTA,
    });

    const fontSizeClass = classNames({
      'text-xs': fontSize === Button.FONT_SIZES.SMALL2X,
      'text-sm': fontSize === Button.FONT_SIZES.SMALL,
      'text-base': fontSize === Button.FONT_SIZES.MEDIUM,
      'text-lg': fontSize === Button.FONT_SIZES.LARGE,
      'text-xl': fontSize === Button.FONT_SIZES.LARGE2X,
      'text-base leading-5': fontSize === Button.FONT_SIZES.BASE,
    });

    const fontWeightClass = fontWeight?.startsWith('font-')
      ? fontWeight
      : classNames({
          'font-normal': fontWeight === Button.FONT_WEIGHTS.NORMAL,
          'font-medium': fontWeight === Button.FONT_WEIGHTS.MEDIUM || !fontWeight,
          'font-semibold': fontWeight === Button.FONT_WEIGHTS.SEMIBOLD,
          'font-bold': fontWeight === Button.FONT_WEIGHTS.BOLD,
        });

    const variantClass = classNames({
      'bg-primary hover:bg-primary-400 text-white': variant === Button.VARIANTS.PRIMARY,
      'bg-primary-400 text-black': variant === Button.VARIANTS.PRIMARY && (disabled || isLoading),
      'border-primary ring-1 ring-primary ring-inset hover:bg-primary text-primary hover:text-black':
        variant === Button.VARIANTS.PRIMARY_OUTLINE,
      'bg-secondary-500 hover:bg-secondary-600 text-white ': variant === Button.VARIANTS.SECONDARY,
      'bg-secondary-500 hover:bg-secondary-600 text-white': variant === Button.VARIANTS.SECONDARY_2,
      'bg-secondary-500 text-white': variant === Button.VARIANTS.SECONDARY && (disabled || isLoading),
      'border-secondary ring-1 ring-secondary ring-inset hover:bg-secondary hover:text-black text-secondary':
        variant === Button.VARIANTS.SECONDARY_OUTLINE,
      'bg-tertiary hover:bg-tertiary-400 text-white': variant === Button.VARIANTS.TERTIARY,
      'bg-tertiary-100 hover:bg-tertiary-200 text-tertiary-400': variant === Button.VARIANTS.TERTIARY_LIGHT,
      'border-tertiary ring-1 ring-tertiary ring-inset hover:bg-tertiary text-tertiary hover:text-white':
        variant === Button.VARIANTS.TERTIARY_OUTLINE,
      'bg-gray-100 hover:bg-gray-200 text-white': variant === Button.VARIANTS.GRAY,
      'border-gray-300 ring-1 ring-gray-400 ring-inset hover:bg-gray-100':
        variant === Button.VARIANTS.GRAY_OUTLINE,
      'bg-white hover:bg-gray-100 text-primary': variant === Button.VARIANTS.WHITE,
      'border-white ring-1 ring-white ring-inset hover:bg-white text-white hover:text-primary':
        variant === Button.VARIANTS.WHITE_OUTLINE,
      'bg-red-500 hover:bg-red-400 text-white': variant === Button.VARIANTS.DANGER,
      'border-2 hover:border-1 border-red-600 hover:bg-red-100 text-red-600 hover:text-white':
        variant === Button.VARIANTS.DANGER_OUTLINE,
      'bg-white text-black hover:bg-gray-100 font-secondary': variant === Button.VARIANTS.WHITE_SOLID_BLACK_TEXT,
      'bg-transparent border border-white text-white hover:bg-white hover:text-black font-secondary':
        variant === Button.VARIANTS.TRANSPARENT_WHITE_OUTLINE,
      'bg-transparent text-primary': variant === Button.VARIANTS.GHOST && !(disabled || isLoading),
      'bg-transparent text-primary/50 cursor-not-allowed':
        variant === Button.VARIANTS.GHOST && (disabled || isLoading),
    });

    if (linkTo) {
      return (
        <Link
          ref={ref}
          to={linkTo}
          className={`${mainClass} ${variantClass} ${fontSizeClass} ${sizeClass} ${fontWeightClass} ${className} flex`}
          onClick={onClick}
          {...props}
        >
          {isLoading && <BiLoaderAlt className="mr-3 animate-spin" />}
          {children}
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        className={`${mainClass} ${variantClass} ${fontSizeClass} ${sizeClass} ${fontWeightClass} ${className} flex`}
        onClick={onClick}
        type={submit ? 'submit' : 'button'}
        disabled={isLoading || disabled}
        {...props}
      >
        {isLoading && <BiLoaderAlt className="mr-3 animate-spin" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

Button.SIZES = {
  SMALL2X: 'SMALL2X',
  SMALL: 'SMALL',
  SQUARE: 'SQUARE',
  MEDIUM: 'MEDIUM',
  LARGE: 'LARGE',
  LARGE2X: 'LARGE2X',
  CTA: 'CTA',
};

Button.VARIANTS = {
  PRIMARY: 'PRIMARY',
  SECONDARY: 'SECONDARY',
  SECONDARY_2: 'SECONDARY_2',
  TERTIARY: 'TERTIARY',
  TERTIARY_LIGHT: 'TERTIARY_LIGHT',
  PRIMARY_OUTLINE: 'PRIMARY_OUTLINE',
  SECONDARY_OUTLINE: 'SECONDARY_OUTLINE',
  TERTIARY_OUTLINE: 'TERTIARY_OUTLINE',
  GRAY: 'GRAY',
  GRAY_OUTLINE: 'GRAY_OUTLINE',
  WHITE: 'WHITE',
  WHITE_OUTLINE: 'WHITE_OUTLINE',
  DANGER: 'DANGER',
  DANGER_OUTLINE: 'DANGER_OUTLINE',
  WHITE_SOLID_BLACK_TEXT: 'WHITE_SOLID_BLACK_TEXT',
  TRANSPARENT_WHITE_OUTLINE: 'TRANSPARENT_WHITE_OUTLINE',
  GHOST: 'GHOST',
};

Button.FONT_SIZES = {
  SMALL2X: 'SMALL2X',
  SMALL: 'SMALL',
  MEDIUM: 'MEDIUM',
  LARGE: 'LARGE',
  LARGE2X: 'LARGE2X',
  BASE: 'BASE',
};

Button.FONT_WEIGHTS = {
  NORMAL: 'NORMAL',
  MEDIUM: 'MEDIUM',
  SEMIBOLD: 'SEMIBOLD',
  BOLD: 'BOLD',
};

Button.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  size: PropTypes.oneOf(Object.values(Button.SIZES)),
  fontSize: PropTypes.oneOf(Object.values(Button.FONT_SIZES)),
  variant: PropTypes.oneOf(Object.values(Button.VARIANTS)),
  fontWeight: PropTypes.oneOfType([PropTypes.oneOf(Object.values(Button.FONT_WEIGHTS)), PropTypes.string]),
  submit: PropTypes.bool,
  onClick: PropTypes.func,
  linkTo: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
};

Button.defaultProps = {
  className: '',
  children: '',
  size: Button.SIZES.MEDIUM,
  fontSize: Button.FONT_SIZES.SMALL,
  variant: Button.VARIANTS.PRIMARY,
  onClick: () => {},
  submit: false,
  linkTo: null,
  fontWeight: Button.FONT_WEIGHTS.MEDIUM,
  disabled: false,
  isLoading: false,
};

export default Button;
